import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import useSiteSettings from '../../hooks/useSiteSettings';
import HubSpotForm from '../common/HubSpotForm';

interface FloatingContactIconsProps {
  showOnAdmin?: boolean;
}

const FloatingContactIcons: React.FC<FloatingContactIconsProps> = ({ 
  showOnAdmin = false 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [formData, setFormData] = useState({ name: '', mobile: '', email: '', unitType: '' });
  const [countryCode, setCountryCode] = useState('+233');

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  if (!showOnAdmin && window.location.pathname.includes('/admin')) {
    return null;
  }

  // Site ayarlarından iletişim bilgilerini al
  const siteSettings = useSiteSettings();
  
  // Admin panelden gelen değerler veya varsayılan değerler
  const phoneNumber = siteSettings?.phone || '+971123456789';
  const whatsappNumber = siteSettings?.whatsapp || '971123456789';
  const whatsappString = String(whatsappNumber || '');
  const whatsappNumberClean = whatsappString.startsWith('+') ? whatsappString.substring(1) : whatsappString;
  const emailAddress = siteSettings?.email || 'info@goldentower.com';
  const pdfUrl = siteSettings?.pdf_url || '/catalog.pdf';

  // no effect download; handled directly on submit success

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.mobile.trim() || !formData.email.trim()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await api.post('/forms/submit', {
        form_type: 'register_interest',
        name: formData.name,
        mobile: `${countryCode}${formData.mobile}`,
        email: formData.email,
        unit_type: formData.unitType,
        country_code: countryCode
      });
      if (!response.success) throw new Error(response.error || 'Submission failed');
      // trigger download immediately to avoid effect loops
      if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setSubmitStatus('success');
      setFormData({ name: '', mobile: '', email: '', unitType: '' });
      setTimeout(() => setIsModalOpen(false), 800);
    } catch (err) {
      console.error(err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="hidden md:flex fixed right-6 bottom-32 z-50 flex-col gap-4">
        <a
          href={`tel:${phoneNumber}`}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Call Us"
        >
          <i className="ri-phone-fill text-white text-xl"></i>
        </a>
        <a
          href={`https://wa.me/${whatsappNumberClean}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#25D366] hover:border-[#25D366] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="WhatsApp"
        >
          <i className="ri-whatsapp-fill text-white text-xl"></i>
        </a>
        <a
          href={`mailto:${emailAddress}`}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Email Us"
        >
          <i className="ri-mail-fill text-white text-xl"></i>
        </a>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Download Catalog"
        >
          <i className="ri-file-pdf-fill text-white text-xl"></i>
        </button>
      </div>

      <div className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-black/80 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <a
              href={`tel:${phoneNumber}`}
              className="w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center active:scale-95 transition"
              title="Call Us"
            >
              <i className="ri-phone-fill text-white text-lg"></i>
            </a>
            <a
              href={`https://wa.me/${whatsappNumberClean}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center active:scale-95 transition"
              title="WhatsApp"
            >
              <i className="ri-whatsapp-fill text-white text-lg"></i>
            </a>
            <a
              href={`mailto:${emailAddress}`}
              className="w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center active:scale-95 transition"
              title="Email Us"
            >
              <i className="ri-mail-fill text-white text-lg"></i>
            </a>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center active:scale-95 transition"
              title="Download Catalog"
            >
              <i className="ri-file-pdf-fill text-white text-lg"></i>
            </button>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-black border border-gray-700 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-light text-white">REGISTER NOW FOR <span className="font-bold">MORE DETAILS</span></h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <HubSpotForm
                onSuccess={() => {
                  if (pdfUrl) {
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = '';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                  setTimeout(() => setIsModalOpen(false), 1200);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingContactIcons;

// Export the modal component for reuse in Header and Brand page
export const DownloadCatalogModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
}> = ({ isOpen, onClose, pdfUrl }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSuccess = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setTimeout(() => onClose(), 1200);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[250] flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}
    >
      <div
        className="bg-black border border-gray-700 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h3 className="text-xl font-light text-white">REGISTER NOW FOR <span className="font-bold">MORE DETAILS</span></h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <div className="p-6">
          <HubSpotForm onSuccess={handleSuccess} />
        </div>
      </div>
    </div>
  );
};
